import { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Divider,
  useTheme,
  useScrollTrigger,
  alpha,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import init100 from '../assets/init100.png';
import { modules } from '../data/modules';

const pages = [
  { name: 'Home', path: '/' },
  { name: 'Features', path: '/features' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const CONNECT_URL = 'https://connect.solvenda.org';

const Navbar = ({ mode, setMode }) => {
  const theme = useTheme();
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElProducts, setAnchorElProducts] = useState(null);

  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 50 });

  const handleThemeChange = () => setMode(mode === 'dark' ? 'light' : 'dark');
  const isActive = (path) => location.pathname === path;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: trigger ? alpha(theme.palette.background.default, 0.85) : 'transparent',
        backdropFilter: trigger ? 'saturate(180%) blur(12px)' : 'none',
        borderBottom: trigger ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
        py: trigger ? 0.25 : 0.75,
        transition: 'all 0.3s ease',
      }}
    >
      <Container>
        <Toolbar disableGutters>
          {/* Desktop Logo */}
          <Box
            component={Link}
            to="/"
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', textDecoration: 'none', color: 'text.primary' }}
          >
            <Box component="img" src={init100} alt="SOLVENDA Logo" sx={{ height: 38, mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 800, fontSize: '1.4rem', letterSpacing: '0.02em' }}>
              SOLVENDA
            </Typography>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton size="large" onClick={(e) => setAnchorElNav(e.currentTarget)} color="inherit" sx={{ color: 'text.primary' }}>
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(anchorElNav)}
              onClose={() => setAnchorElNav(null)}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {pages.map((page) => (
                <MenuItem key={page.name} onClick={() => setAnchorElNav(null)} component={Link} to={page.path} selected={isActive(page.path)}>
                  {page.name}
                </MenuItem>
              ))}
              <MenuItem onClick={() => setAnchorElNav(null)} component={Link} to="/connect" selected={isActive('/connect')}>
                SolvendaConnect
              </MenuItem>
              <Divider />
              <MenuItem component="a" href={CONNECT_URL} target="_blank" rel="noopener noreferrer" onClick={() => setAnchorElNav(null)} sx={{ color: 'secondary.main', fontWeight: 700 }}>
                <CreditScoreIcon fontSize="small" sx={{ mr: 1 }} /> Pay a bill
              </MenuItem>
            </Menu>
          </Box>

          {/* Mobile Logo */}
          <Box
            component={Link}
            to="/"
            sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', flexGrow: 1, textDecoration: 'none', color: 'text.primary' }}
          >
            <Box component="img" src={init100} alt="SOLVENDA Logo" sx={{ height: 32, mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 800 }}>
              SOLVENDA
            </Typography>
          </Box>

          {/* Desktop Nav Links */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 0.5 }}>
            <Button component={Link} to="/" sx={navLinkSx(theme, isActive('/'))}>Home</Button>

            {/* Products dropdown */}
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setAnchorElProducts(e.currentTarget)}
              sx={navLinkSx(theme, isActive('/features'))}
            >
              Products
            </Button>
            <Menu
              anchorEl={anchorElProducts}
              open={Boolean(anchorElProducts)}
              onClose={() => setAnchorElProducts(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 260, borderRadius: 2, border: `1px solid ${theme.palette.divider}` } } }}
            >
              {modules.map((m) => {
                const to = m.id === 'connect' ? '/connect' : '/features';
                return (
                  <MenuItem
                    key={m.id}
                    component={Link}
                    to={to}
                    onClick={() => setAnchorElProducts(null)}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.color, mr: 1.5 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{m.tagline}</Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>

            <Button component={Link} to="/pricing" sx={navLinkSx(theme, isActive('/pricing'))}>Pricing</Button>
            <Button component={Link} to="/about" sx={navLinkSx(theme, isActive('/about'))}>About</Button>
            <Button component={Link} to="/contact" sx={navLinkSx(theme, isActive('/contact'))}>Contact</Button>
          </Box>

          {/* Right side: Pay a bill + theme toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              component="a"
              href={CONNECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
              startIcon={<CreditScoreIcon />}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Pay a bill
            </Button>
            <IconButton onClick={handleThemeChange} sx={{ color: 'text.primary' }} aria-label="toggle theme">
              {mode === 'dark' ? <Brightness7Icon sx={{ color: '#FFC107' }} /> : <Brightness4Icon sx={{ color: '#5A6675' }} />}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const navLinkSx = (theme, active) => ({
  px: 1.75,
  py: 1,
  color: active ? 'primary.main' : 'text.primary',
  fontWeight: 600,
  position: 'relative',
  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
  '&::after': active
    ? {
        content: '""',
        position: 'absolute',
        bottom: 6,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 18,
        height: 3,
        bgcolor: 'primary.main',
        borderRadius: 2,
      }
    : {},
});

export default Navbar;
